import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AppRole, storeActiveRole } from '../lib/roles';
import { OrganisationBlockedError } from '../lib/authErrors';
import {
  completeNewPassword,
  confirmEmailSignUp,
  getAuthenticatedUser,
  resendVerificationCode,
  signInWithEmail,
  signOutUser,
  signUpWithEmail,
} from '../services/auth';
import { AuthUser, SignInResult, SignUpResult } from '../types/auth';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  hasRole: (roles: AppRole[]) => boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  completeFirstLoginPassword: (newPassword: string) => Promise<AuthUser>;
  signUp: (email: string, password: string) => Promise<SignUpResult>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
  refreshUser: () => Promise<AuthUser | null>;
  setActiveRole: (role: AppRole) => void;
  updateProfile: (data: { fullName: string; designation: string; department?: string }) => Promise<AuthUser>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await getAuthenticatedUser();
      setUser(currentUser);
      return currentUser;
    } catch (error) {
      setUser(null);
      if (error instanceof OrganisationBlockedError) {
        throw error;
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshUser().catch(() => {
      // OrganisationBlockedError and other failures leave user null; sign-in page shows notices.
    });
  }, [refreshUser]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      try {
        const result: SignInResult = await signInWithEmail(email, password);

        if (result.nextStep === 'DONE') {
          let currentUser = result.user ?? (await refreshUser());

          if (!currentUser) {
            throw new Error('Sign in completed, but the user session could not be loaded.');
          }

          if (currentUser.availableRoles.length === 1 && currentUser.availableRoles[0]) {
            storeActiveRole(currentUser.availableRoles[0]);
            currentUser = { ...currentUser, activeRole: currentUser.availableRoles[0], role: currentUser.availableRoles[0] };
          }

          setUser(currentUser);

          return {
            ...result,
            user: currentUser,
          };
        }

        return result;
      } catch (error) {
        setUser(null);
        throw error;
      }
    },
    [refreshUser],
  );

  const completeFirstLoginPassword = useCallback(
    async (newPassword: string) => {
      try {
        const result = await completeNewPassword(newPassword);

        if (result.nextStep !== 'DONE') {
          throw new Error(`Unexpected sign-in step: ${result.nextStep}`);
        }

        let currentUser = result.user ?? (await refreshUser());
        if (!currentUser) {
          throw new Error('Password updated, but the user session could not be loaded.');
        }

        if (currentUser.availableRoles.length === 1 && currentUser.availableRoles[0]) {
          storeActiveRole(currentUser.availableRoles[0]);
          currentUser = { ...currentUser, activeRole: currentUser.availableRoles[0], role: currentUser.availableRoles[0] };
        }

        setUser(currentUser);

        return currentUser;
      } catch (error) {
        setUser(null);
        throw error;
      }
    },
    [refreshUser],
  );

  const signUp = useCallback(async (email: string, password: string) => {
    return signUpWithEmail({ email, password });
  }, []);

  const confirmSignUp = useCallback(async (email: string, code: string) => {
    await confirmEmailSignUp(email, code);
  }, []);

  const resendCode = useCallback(async (email: string) => {
    await resendVerificationCode(email);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await signOutUser();
    } finally {
      storeActiveRole(null);
      setUser(null);
    }
  }, []);

  const setActiveRole = useCallback((role: AppRole) => {
    storeActiveRole(role);
    setUser((current) => (current ? { ...current, activeRole: role, role } : current));
  }, []);

  const updateProfile = useCallback(
    async (data: { fullName: string; designation: string; department?: string }) => {
      const { authAPI } = await import('../services/api');
      await authAPI.updateProfile(data);
      const updated = await refreshUser();
      if (!updated) {
        throw new Error('Profile saved but the session could not be refreshed.');
      }
      return updated;
    },
    [refreshUser],
  );

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      hasRole: (roles: AppRole[]) => (user?.activeRole ? roles.includes(user.activeRole) : false),
      signIn,
      completeFirstLoginPassword,
      signUp,
      confirmSignUp,
      resendCode,
      signOut,
      loading,
      refreshUser,
      setActiveRole,
      updateProfile,
    }),
    [
      user,
      loading,
      signIn,
      completeFirstLoginPassword,
      signUp,
      confirmSignUp,
      resendCode,
      signOut,
      refreshUser,
      setActiveRole,
      updateProfile,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
